<template>
    <template v-if="hasVisibleActions">
        <div class="icon-flex">
            <action-button
                v-if="showHelpAction"
                class="icon-controls"
                title="What do the series on this chart mean?"
                :icon="['fas', 'question']"
                show-title-on-mobile-view
                @click="$event => openPopover($event, 'series')"
            />
            <action-button
                v-if="showDownloadAction"
                class="icon-controls"
                title="Download"
                :icon="['fas', 'download']"
                show-title-on-mobile-view
                @click="$event => openPopover($event, 'download')"
            />
            <action-button
                v-if="showAxisOptionsAction"
                class="icon-controls"
                title="Axis options"
                :icon="['fas', 'chart-simple']"
                show-title-on-mobile-view
                @click="$event => openAxisOptions($event)"
            />
            <action-button
                v-if="showCurrencyAction"
                class="icon-controls"
                title="Currency"
                :icon="['fas', 'circle-sterling']"
                show-title-on-mobile-view
                @click="$event => openCurrencyOptions($event)"
            />
            <action-button
                v-if="showEditTableAction"
                class="icon-controls"
                title="Edit table"
                :icon="['fas', 'pencil']"
                show-title-on-mobile-view
                @click="toggleTableEdit"
            />
            <action-button
                v-if="showGraphAction"
                class="icon-controls"
                title="Show as a graph"
                :icon="['fad', 'chart-line']"
                show-title-on-mobile-view
                @click="() => setCurrentView(ViewMode.Chart)"
            />
            <action-button
                v-if="showTableAction"
                class="icon-controls"
                title="Show as a table"
                :icon="['fas', 'table']"
                show-title-on-mobile-view
                @click="() => setCurrentView(ViewMode.Table)"
            />
            <action-button
                v-if="showMetricsAction"
                class="icon-controls"
                title="Show metrics"
                :icon="['fas', 'list']"
                show-title-on-mobile-view
                list-mode-on-touch
                @click="() => setCurrentView(ViewMode.Metrics)"
            />
        </div>
        <popover ref="optionsPopover" dismissable @hide="props.updatePopoverVisibility(false)" :base-z-index="10000">
            <div class="popover-wrapper">
                <template v-if="popoverDisplayed === 'series'">
                    <h4>Series Definitions</h4>
                    <div class="column-list">
                        <ul>
                            <li
                                v-for="series in props.chartConfig.data.uniqueSeriesNoOption"
                                :key="series.id"
                            >
                                <primevue-button
                                    class="definition-button plain-btn"
                                    @click="openDefinitionModal(series.id)"
                                >
                                    {{ series.name }}
                                    <font-awesome-icon class="icon" :icon="['fas', 'arrow-right']" />
                                </primevue-button>
                            </li>
                        </ul>
                    </div>
                </template>
                <template v-else-if="popoverDisplayed === 'download'">
                    <primevue-button
                        class="plain-btn"
                        @click="toggleTableDownload"
                        :loading="props.isTableDownloadLoading"
                        :disabled="props.isTableDownloadLoading"
                        v-if="!props.blockedActions?.download && !props.noData"
                    >
                        {{ props.isTableDownloadLoading ? 'Preparing download' : 'Download data' }}
                        <font-awesome-icon class="icon" :icon="['fas', 'download']" />
                    </primevue-button>
                    <primevue-button
                        class="plain-btn"
                        @click="downloadChart"
                        v-if="!props.blockedActions?.download && !props.noData && props.currentView === ViewMode.Chart && props.chartConfig"
                    >
                        Download image
                        <font-awesome-icon class="icon" :icon="['fas', 'download']" />
                    </primevue-button>
                </template>
            </div>
        </popover>
        <definition-modal
            v-if="activeDefinition !== null"
            :definition="activeDefinition"
            @close:modal="activeDefinition = null"
        />
        <popover ref="axisOptionsPopover" :base-z-index="10000" @hide="props.updatePopoverVisibility(false)">
            <div class="popover-wrapper">
                <chart-axis-limits-modal
                    :y-axis-array="props.yAxisArray ?? []"
                    :current-time-line="props.verticalLine"
                    :show-time="props.showTime"
                    :show-legend="props.showLegend"
                    :multiple-axis-options="localMultipleAxisOptions"
                    @y-axis-limits="updateYAxisLimits"
                    @multiple-axis-options="updateMultipleAxisOptions"
                    @toggle-x-axis="toggleXAxis"
                    @toggle-current-line="toggleCurrentLine"
                    @toggle-legend="toggleLegend"
                    @can-save-axis-changes="updateCanSaveAxisChanges"
                />
                <div class="footer-row">
                    <primevue-button
                        v-tooltip="axisUpdateDisabledReason"
                        class="plain-btn"
                        :disabled="!canSaveAxisChanges"
                        @click="submitAxisOptions"
                    >
                        Update axis options
                        <font-awesome-icon :icon="['fas', 'floppy-disk']" />
                    </primevue-button>
                </div>
            </div>
        </popover>
        <popover ref="currencyOptionsPopover" :base-z-index="10000" @hide="props.updatePopoverVisibility(false)">
            <currency-selector class="currency-selector" v-model="localCurrencyId" @update:model-value="submitCurrencyOptions" />
        </popover>
    </template>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import PrimevueButton from 'primevue/button';
import Popover from 'primevue/popover';
import { IDefinition } from '@/interfaces/powerMarkets/static/IDefinitionSearchInfo.ts';
import {
    IAxisLimits, IModuleChartConfig, ViewMode,
} from '@/interfaces/powerMarkets/charts/IModuleChartConfig.ts';
import { IModuleOptionsProps } from '@/interfaces/powerMarkets/charts/IModuleOptionsProps.ts';
import { useDefinitionStore } from '@/stores/powerMarkets/definitionStore.ts';
import DefinitionModal from '@/components/powerMarkets/modals/DefinitionModal.vue';
import { IModuleBlockedActions } from '@/interfaces/actions/enact/IModuleActions.ts';
import { IChartMultipleAxisOptions } from '@/interfaces/powerMarkets/charts/IChartMultipleAxisOptions.ts';
import ActionButton from '../common/ActionButton.vue';
import ChartAxisLimitsModal from './ChartAxisLimitsModal.vue';
import CurrencySelector from './CurrencySelector.vue';

const definitionStore = useDefinitionStore();

interface IProps extends IModuleOptionsProps {
    chartConfig: IModuleChartConfig;
    currentView: ViewMode;
    canMetric?: boolean;
    updatePopoverVisibility: (value: boolean) => void;
    noData: boolean;
    isTableDownloadLoading?: boolean;
    blockedActions?: IModuleBlockedActions;
    disableCurrencyConversion?: boolean;
    disableAxisOptions?: boolean;
    currencyOverride?: string;
    verticalLine?: string;
    showTime?: boolean;
    showLegend?: boolean;
    yAxisArray?: IAxisLimits[];
    multipleAxisOptions?: IChartMultipleAxisOptions | null;
}

const props = defineProps<IProps>();

const emit = defineEmits<{
    'downloadChart': [];
    'set:current-view': [ViewMode];
    'refresh-chart': [];
    'toggle-table-edit': [Event];
    'toggle-table-download': [];
    'update:y-axis-limits': [IAxisLimits[]];
    'update:x-axis-current-line': [string];
    'toggle:x-axis': [];
    'update:show-legend': [boolean];
    'update:currency': [string];
    'update:multiple-axis-options': [IChartMultipleAxisOptions];
}>();

type popoverTypes = 'series' | 'download';

const optionsPopover = ref<InstanceType<typeof Popover> | null>(null);
const axisOptionsPopover = ref<InstanceType<typeof Popover> | null>(null);
const currencyOptionsPopover = ref<InstanceType<typeof Popover> | null>(null);

const activeDefinition = ref<IDefinition | null | undefined>(null);

const popoverDisplayed = ref<popoverTypes | null>(null);
const localShowTime = ref<boolean>(props.showTime ?? true);
const localShowLegend = ref<boolean>(props.showLegend ?? true);
const localVerticalLine = ref<string>(props.verticalLine ?? 'period');
const localYAxisArray = ref<Array<IAxisLimits>>(props.yAxisArray ?? []);
const yAxisLimitsChanged = ref(false);
const localMultipleAxisOptions = ref<IChartMultipleAxisOptions | null>(cloneMultipleAxisOptions(props.multipleAxisOptions ?? null));
const canSaveAxisChanges = ref<boolean>(true);
const localCurrencyId = ref<string>(props.currencyOverride ?? 'default');

const showHelpAction = computed<boolean>(() => !props.blockedActions?.explain && props.chartConfig.data?.uniqueSeriesNoOption.length > 0);
const showDownloadAction = computed<boolean>(() => !props.blockedActions?.download && !props.noData);
const showAxisOptionsAction = computed<boolean>(() => !props.disableAxisOptions);
const showCurrencyAction = computed<boolean>(() => !props.disableCurrencyConversion && currencyList.value.size > 0);
const showEditTableAction = computed<boolean>(() => !props.noData && props.currentView === ViewMode.Table && !props.blockedActions?.edit);
const showGraphAction = computed<boolean>(() => !props.noData && props.currentView !== ViewMode.Chart && (props.canTable || props.canMetric));
const showTableAction = computed<boolean>(() => !props.noData && props.currentView !== ViewMode.Table && props.canTable);
const showMetricsAction = computed<boolean>(() => !props.noData && props.currentView !== ViewMode.Metrics && !props.showMetricsInsteadOfChart && props.canMetric);
const hasVisibleActions = computed<boolean>(() => (
    showHelpAction.value
    || showDownloadAction.value
    || showAxisOptionsAction.value
    || showCurrencyAction.value
    || showEditTableAction.value
    || showGraphAction.value
    || showTableAction.value
    || showMetricsAction.value
));
const currencyList = computed<Set<string>>(() => {
    const list = new Set<string>();

    if (!props.chartConfig || !props.chartConfig.data || !props.chartConfig.data.config) return list;

    props.chartConfig.data.config.forEach(configValue => {
        if (!configValue.currency) return;
        list.add(configValue.currency);
    });

    return list;
});
const axisUpdateDisabledReason = computed<string>(() => (canSaveAxisChanges.value ? '' : 'Axis limits are not valid.'));

function openPopover (event: Event, popoverType: popoverTypes): void {
    axisOptionsPopover.value?.hide();
    currencyOptionsPopover.value?.hide();
    popoverDisplayed.value = popoverType;
    optionsPopover.value?.toggle(event);
    props.updatePopoverVisibility(true);
}

async function openDefinitionModal (definitionId: string): Promise<void> {
    optionsPopover.value?.hide();
    activeDefinition.value = await definitionStore.getDefinitionById(definitionId);
}

function downloadChart (): void {
    emit('downloadChart');
}

function setCurrentView (viewMode: ViewMode): void {
    emit('set:current-view', viewMode);
}

function toggleTableEdit (event: Event): void {
    emit('toggle-table-edit', event);
}

function toggleTableDownload (): void {
    emit('toggle-table-download');
}

function cloneMultipleAxisOptions (multipleAxisOptions: IChartMultipleAxisOptions | null | undefined): IChartMultipleAxisOptions | null {
    if (!multipleAxisOptions) return null;

    return {
        axisStackType: multipleAxisOptions.axisStackType,
        axisSide: multipleAxisOptions.axisSide,
        axisPositions: multipleAxisOptions.axisPositions?.map(axis => ({ ...axis })) ?? null,
        axisLeftOrRight: multipleAxisOptions.axisLeftOrRight.map(axis => ({ ...axis })),
    };
}

function areMultipleAxisOptionsEquivalent (
    current: IChartMultipleAxisOptions | null | undefined,
    next: IChartMultipleAxisOptions | null,
): boolean {
    if (!current && !next) return true;
    if (!current || !next) return false;
    if (current.axisStackType !== next.axisStackType) return false;
    if (current.axisSide !== next.axisSide) return false;

    const currentPositions = current.axisPositions ?? [];
    const nextPositions = next.axisPositions ?? [];
    if (currentPositions.length !== nextPositions.length) return false;

    const positionsMatch = currentPositions.every((axisPosition, index) => {
        const nextPosition = nextPositions[index];
        return axisPosition.label === nextPosition.label
            && axisPosition.value === nextPosition.value
            && axisPosition.edited === nextPosition.edited;
    });

    if (!positionsMatch) return false;
    if (current.axisLeftOrRight.length !== next.axisLeftOrRight.length) return false;

    return current.axisLeftOrRight.every((axisPosition, index) => {
        const nextAxisPosition = next.axisLeftOrRight[index];
        return axisPosition.label === nextAxisPosition.label
            && axisPosition.value === nextAxisPosition.value;
    });
}

function syncAxisDrafts (): void {
    localShowTime.value = props.showTime ?? true;
    localShowLegend.value = props.showLegend ?? true;
    localVerticalLine.value = props.verticalLine ?? 'period';
    localYAxisArray.value = props.yAxisArray ?? [];
    yAxisLimitsChanged.value = false;
    localMultipleAxisOptions.value = cloneMultipleAxisOptions(props.multipleAxisOptions ?? null);
    canSaveAxisChanges.value = true;
}

function openAxisOptions (event: Event): void {
    optionsPopover.value?.hide();
    currencyOptionsPopover.value?.hide();
    syncAxisDrafts();
    axisOptionsPopover.value?.toggle(event);
    props.updatePopoverVisibility(true);
}

function openCurrencyOptions (event: Event): void {
    optionsPopover.value?.hide();
    axisOptionsPopover.value?.hide();
    localCurrencyId.value = props.currencyOverride ?? 'default';
    currencyOptionsPopover.value?.toggle(event);
    props.updatePopoverVisibility(true);
}

function updateYAxisLimits (yAxisLimits: Array<IAxisLimits>): void {
    localYAxisArray.value = yAxisLimits;
    yAxisLimitsChanged.value = true;
}

function updateMultipleAxisOptions (multipleAxisOptions: IChartMultipleAxisOptions): void {
    localMultipleAxisOptions.value = cloneMultipleAxisOptions(multipleAxisOptions);
}

function toggleXAxis (): void {
    localShowTime.value = !localShowTime.value;
}

function toggleCurrentLine (xAxisCurrentLine: string): void {
    localVerticalLine.value = xAxisCurrentLine;
}

function toggleLegend (showLegend: boolean): void {
    localShowLegend.value = showLegend;
}

function updateCanSaveAxisChanges (canSave: boolean): void {
    canSaveAxisChanges.value = canSave;
}

function submitAxisOptions (): void {
    if (!canSaveAxisChanges.value) return;

    if (localShowTime.value !== (props.showTime ?? true)) emit('toggle:x-axis');
    if (localVerticalLine.value !== (props.verticalLine ?? 'period')) emit('update:x-axis-current-line', localVerticalLine.value);
    if (localShowLegend.value !== (props.showLegend ?? true)) emit('update:show-legend', localShowLegend.value);
    if (yAxisLimitsChanged.value) emit('update:y-axis-limits', localYAxisArray.value);

    if (!areMultipleAxisOptionsEquivalent(props.multipleAxisOptions, localMultipleAxisOptions.value)) {
        emit('update:multiple-axis-options', localMultipleAxisOptions.value as IChartMultipleAxisOptions);
    }

    axisOptionsPopover.value?.hide();
}

function submitCurrencyOptions (): void {
    emit('update:currency', localCurrencyId.value);
    currencyOptionsPopover.value?.hide();
}

</script>

<style lang="scss" scoped>

.currency-selector {
    margin-bottom: var(--largeSpacing);
}

.popover-wrapper {
    padding: var(--largeSpacing);
    display: grid;
    grid-template-rows: auto 1fr;
    gap: var(--defaultSpacing);
}

.icon-flex {
    display: flex;
    flex-direction: column;
    gap: var(--smallSpacing) 0;
}

.column-list {
    display: flex;
    flex-direction: column;
    gap: var(--defaultSpacing);
    max-height: 300px;
    overflow: auto;
    padding-right: var(--smallSpacing);
    margin-top: var(--smallSpacing);

    ul{
        display: flex;
        flex-direction: column;
        gap: var(--defaultSpacing);

        .definition-button{
            width: 100%;
        }
    }
}

.forecast-history-options{
    position: absolute;
    top: 0;
}

.footer-row {
    display: flex;
    justify-content: flex-end;
    width: 100%;
}

.edit-btn {
    @include responsive('sm') {
        display: none;
    }
}
</style>
